import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Plus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  ArrowLeft,
  FileText,
  Image,
  Loader2,
} from "lucide-react";
import {
  getAdminBlogPosts,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
} from "../../services/api";
import "./ContentStyles.css";

function BlogManagementContent() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, "new" or post object
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Form state
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [postStatus, setPostStatus] = useState("draft");
  const [metaDescription, setMetaDescription] = useState("");
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ header: [2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["blockquote", "link", "image"],
      ["clean"],
    ],
  }), []);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const data = await getAdminBlogPosts();
      if (data.success) {
        setPosts(data.posts);
      }
    } catch {
      setMessage(t("blogAdmin.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle("");
    setExcerpt("");
    setContent("");
    setPostStatus("draft");
    setMetaDescription("");
    setCoverImage(null);
    setCoverPreview("");
  };

  const openNew = () => {
    resetForm();
    setEditing("new");
    setMessage("");
  };

  const openEdit = (post) => {
    setTitle(post.title);
    setExcerpt(post.excerpt || "");
    setContent(post.content || "");
    setPostStatus(post.status);
    setMetaDescription(post.meta_description || "");
    setCoverImage(null);
    setCoverPreview(post.cover_image_url || "");
    setEditing(post);
    setMessage("");
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setMessage(t("blogAdmin.titleRequired"));
      return;
    }
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("excerpt", excerpt);
      formData.append("content", content);
      formData.append("status", postStatus);
      formData.append("meta_description", metaDescription);
      if (coverImage) {
        formData.append("cover_image", coverImage);
      }

      if (editing === "new") {
        await createBlogPost(formData);
        setMessage(t("blogAdmin.created"));
      } else {
        await updateBlogPost(editing.id, formData);
        setMessage(t("blogAdmin.updated"));
      }
      setEditing(null);
      resetForm();
      loadPosts();
    } catch {
      setMessage(t("blogAdmin.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm(t("blogAdmin.confirmDelete"))) return;
    try {
      await deleteBlogPost(postId);
      setMessage(t("blogAdmin.deleted"));
      loadPosts();
    } catch {
      setMessage(t("blogAdmin.deleteError"));
    }
  };

  const handleToggleStatus = async (post) => {
    const newStatus = post.status === "published" ? "draft" : "published";
    try {
      const formData = new FormData();
      formData.append("status", newStatus);
      await updateBlogPost(post.id, formData);
      loadPosts();
    } catch {
      setMessage(t("blogAdmin.saveError"));
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Editor view
  if (editing) {
    return (
      <div style={{ padding: "0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <button
            onClick={() => { setEditing(null); resetForm(); setMessage(""); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
              fontSize: 13, fontWeight: 600, color: "#667eea",
            }}
          >
            <ArrowLeft size={16} /> {t("blogAdmin.back")}
          </button>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>
            {editing === "new" ? t("blogAdmin.newPost") : t("blogAdmin.editPost")}
          </h3>
        </div>

        {message && (
          <div style={{
            padding: "8px 12px", borderRadius: 8, marginBottom: 12,
            fontSize: 12, background: message.includes("❌") ? "#fef2f2" : "#f0fdf4",
            color: message.includes("❌") ? "#dc2626" : "#16a34a",
          }}>
            {message}
          </div>
        )}

        {/* Title */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
            {t("blogAdmin.titleLabel")} *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("blogAdmin.titlePlaceholder")}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8,
              border: "1px solid #d1d5db", fontSize: 14, outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Excerpt */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
            {t("blogAdmin.excerptLabel")}
          </label>
          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder={t("blogAdmin.excerptPlaceholder")}
            rows={2}
            maxLength={500}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8,
              border: "1px solid #d1d5db", fontSize: 13, outline: "none",
              resize: "vertical", boxSizing: "border-box",
            }}
          />
        </div>

        {/* Cover Image */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
            {t("blogAdmin.coverLabel")}
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{
              padding: "8px 14px", borderRadius: 8, border: "1px solid #d1d5db",
              fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <Image size={14} /> {t("blogAdmin.chooseCover")}
              <input type="file" accept="image/*" onChange={handleCoverChange} style={{ display: "none" }} />
            </label>
            {coverPreview && (
              <img src={coverPreview} alt="cover" style={{ height: 48, borderRadius: 8, objectFit: "cover" }} />
            )}
          </div>
        </div>

        {/* Content (Rich Text) */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
            {t("blogAdmin.contentLabel")} *
          </label>
          <div style={{ background: "white", borderRadius: 8, border: "1px solid #d1d5db" }}>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              placeholder={t("blogAdmin.contentPlaceholder")}
              style={{ minHeight: 200 }}
            />
          </div>
        </div>

        {/* Meta Description */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
            {t("blogAdmin.metaLabel")}
            <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: 4 }}>
              ({metaDescription.length}/160)
            </span>
          </label>
          <input
            type="text"
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value.slice(0, 160))}
            placeholder={t("blogAdmin.metaPlaceholder")}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8,
              border: "1px solid #d1d5db", fontSize: 13, outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Status + Save */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
          <select
            value={postStatus}
            onChange={(e) => setPostStatus(e.target.value)}
            style={{
              padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db",
              fontSize: 13, outline: "none", background: "white",
            }}
          >
            <option value="draft">{t("blogAdmin.draft")}</option>
            <option value="published">{t("blogAdmin.published")}</option>
          </select>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "10px 20px", borderRadius: 8, border: "none",
              background: "#667eea", color: "white", fontSize: 13,
              fontWeight: 600, cursor: "pointer", display: "flex",
              alignItems: "center", gap: 6, opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? <Loader2 size={14} className="spinner" /> : <Save size={14} />}
            {saving ? t("blogAdmin.saving") : t("blogAdmin.save")}
          </button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div style={{ padding: "0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
          {t("blogAdmin.subtitle")}
        </p>
        <button
          onClick={openNew}
          style={{
            padding: "8px 14px", borderRadius: 8, border: "none",
            background: "#667eea", color: "white", fontSize: 12,
            fontWeight: 600, cursor: "pointer", display: "flex",
            alignItems: "center", gap: 6,
          }}
        >
          <Plus size={14} /> {t("blogAdmin.newPost")}
        </button>
      </div>

      {message && (
        <div style={{
          padding: "8px 12px", borderRadius: 8, marginBottom: 12,
          fontSize: 12, background: message.includes("❌") ? "#fef2f2" : "#f0fdf4",
          color: message.includes("❌") ? "#dc2626" : "#16a34a",
        }}>
          {message}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Loader2 size={24} className="spinner" style={{ color: "#667eea" }} />
        </div>
      ) : posts.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>
          <FileText size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
          <p style={{ fontSize: 13 }}>{t("blogAdmin.noPosts")}</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {posts.map((post) => (
            <div
              key={post.id}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 16px", background: "white", borderRadius: 10,
                border: "1px solid #e5e7eb",
              }}
            >
              {post.cover_image_url ? (
                <img
                  src={post.cover_image_url}
                  alt=""
                  style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
                />
              ) : (
                <div style={{
                  width: 48, height: 48, borderRadius: 8, flexShrink: 0,
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <FileText size={18} style={{ color: "rgba(255,255,255,0.5)" }} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {post.title}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                  {formatDate(post.created_at)} · {post.author_name}
                </div>
              </div>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 6,
                background: post.status === "published" ? "#dcfce7" : "#fef3c7",
                color: post.status === "published" ? "#16a34a" : "#d97706",
                flexShrink: 0,
              }}>
                {post.status === "published" ? t("blogAdmin.published") : t("blogAdmin.draft")}
              </span>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button
                  onClick={() => handleToggleStatus(post)}
                  title={post.status === "published" ? t("blogAdmin.unpublish") : t("blogAdmin.publish")}
                  style={{
                    padding: 6, borderRadius: 6, border: "1px solid #e5e7eb",
                    background: "white", cursor: "pointer", display: "flex",
                    color: post.status === "published" ? "#d97706" : "#16a34a",
                  }}
                >
                  {post.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  onClick={() => openEdit(post)}
                  style={{
                    padding: 6, borderRadius: 6, border: "1px solid #e5e7eb",
                    background: "white", cursor: "pointer", display: "flex",
                    color: "#667eea",
                  }}
                >
                  <Edit3 size={14} />
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  style={{
                    padding: 6, borderRadius: 6, border: "1px solid #e5e7eb",
                    background: "white", cursor: "pointer", display: "flex",
                    color: "#ef4444",
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BlogManagementContent;
